import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Card, Skeleton } from "@libs/rubick";
import { formatUsageValue } from "@/utils/formatters";
import { DONUT_COLORS, buildConicGradient, shortApiKey } from "./utils";
function ConsumptionBreakdown({ activeApiKeysCount, donutSegments, isLoading, keyNameMap, keyUsageTotalsCount, topTenKeys, totalUsageValue, usageUnit }) {
    return /*#__PURE__*/ _jsxs(Card, {
        variant: "bordered",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-4 flex flex-wrap items-center justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-lg font-semibold text-gray-900",
                                children: "Consumption Breakdown"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mt-1 text-sm text-gray-500",
                                children: "Top API keys by usage for the selected period"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-500",
                        children: [
                            activeApiKeysCount.toLocaleString(),
                            " total keys"
                        ]
                    })
                ]
            }),
            isLoading ? /*#__PURE__*/ _jsx(ConsumptionBreakdownSkeleton, {}) : /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 gap-6 xl:grid-cols-[320px_1fr]",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex items-center justify-center",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "relative h-72 w-72 rounded-full",
                            style: {
                                background: buildConicGradient(donutSegments)
                            },
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "absolute inset-14 flex flex-col items-center justify-center rounded-full border border-gray-200 bg-white",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-2xl font-semibold text-gray-900",
                                        children: formatUsageValue(totalUsageValue, usageUnit)
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "mt-1 text-xs text-gray-500",
                                        children: [
                                            keyUsageTotalsCount.toLocaleString(),
                                            " keys in period"
                                        ]
                                    })
                                ]
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-3",
                        children: [
                            topTenKeys.length === 0 && /*#__PURE__*/ _jsx("div", {
                                className: "rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500",
                                children: "No API key usage was recorded in this period."
                            }),
                            topTenKeys.map((item, index)=>{
                                const usagePercent = totalUsageValue > 0 ? item.totalUsageValue / totalUsageValue * 100 : 0;
                                const label = keyNameMap.get(item.apiKeyId) || shortApiKey(item.apiKeyId);
                                return /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center justify-between text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsxs("p", {
                                                    className: "truncate pr-4 text-gray-700",
                                                    children: [
                                                        index + 1,
                                                        ". ",
                                                        label
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "text-gray-500",
                                                    children: formatUsageValue(item.totalUsageValue, usageUnit)
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "h-2 overflow-hidden rounded bg-gray-100",
                                            children: /*#__PURE__*/ _jsx("div", {
                                                className: "h-full rounded",
                                                style: {
                                                    width: `${Math.max(usagePercent, 1)}%`,
                                                    backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length]
                                                }
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "text-right text-xs text-gray-500",
                                            children: [
                                                usagePercent.toFixed(1),
                                                "%"
                                            ]
                                        })
                                    ]
                                }, item.apiKeyId);
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
function ConsumptionBreakdownSkeleton() {
    return /*#__PURE__*/ _jsxs("div", {
        className: "grid grid-cols-1 gap-6 xl:grid-cols-[320px_1fr]",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex items-center justify-center",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "relative h-72 w-72",
                    children: [
                        /*#__PURE__*/ _jsx(Skeleton, {
                            className: "h-72 w-72 rounded-full"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "absolute inset-14 flex flex-col items-center justify-center rounded-full bg-white",
                            children: [
                                /*#__PURE__*/ _jsx(Skeleton, {
                                    className: "h-7 w-20 rounded"
                                }),
                                /*#__PURE__*/ _jsx(Skeleton, {
                                    className: "mt-2 h-4 w-24 rounded"
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-3",
                children: Array.from({
                    length: 5
                }).map((_, index)=>/*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between",
                                children: [
                                    /*#__PURE__*/ _jsx(Skeleton, {
                                        className: "h-4 w-36 rounded"
                                    }),
                                    /*#__PURE__*/ _jsx(Skeleton, {
                                        className: "h-4 w-16 rounded"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Skeleton, {
                                className: "h-2 w-full rounded"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex justify-end",
                                children: /*#__PURE__*/ _jsx(Skeleton, {
                                    className: "h-3 w-10 rounded"
                                })
                            })
                        ]
                    }, index))
            })
        ]
    });
}
export { ConsumptionBreakdown };
